import type { MyBookingDetail } from '@massilia/queries'

import { paymentDeadlineTime } from '@massilia/contracts'
import { Button, Separator } from '@massilia/ui'
import { Link } from '@tanstack/react-router'
import { CalendarClock, CreditCard } from 'lucide-react'
import { useMemo } from 'react'

import { SummaryRow } from '@/components/payment-confirmation/summary-row'
import { BankDetailsGrid } from '@/components/shared/bank-details-grid'
import { centsToEuros, formatPrice } from '@/utils/format-price'

interface PaymentSettlementSectionProps {
  booking: MyBookingDetail
}

export function PaymentSettlementSection({ booking }: PaymentSettlementSectionProps) {
  const isAwaitingDeposit = booking.status === 'AWAITING_DEPOSIT'
  // Coordonnées bancaires de l'agence qui suit le dossier (transférable).
  const isReunion = booking.agency === 'RUN'

  const paidAmount = useMemo(
    () => booking.payments.filter((p) => p.status === 'PAID').reduce((sum, p) => sum + p.amount, 0),
    [booking.payments]
  )

  const remainingAmount = booking.totalPrice - paidAmount

  // Prestataire du dossier, pas le flag global : FLOA après un refus Oney.
  const installmentProviderLabel = booking.installmentProvider === 'FLOA' ? 'FLOA' : 'Oney'

  const balanceDeadline = isAwaitingDeposit
    ? booking.depositDeadline
    : (booking.paymentDeadline ?? booking.depositDeadline)
  const deadlineDate = formatDeadline(balanceDeadline)
  const deadlineLabel =
    deadlineDate && balanceDeadline
      ? `${deadlineDate} avant ${paymentDeadlineTime(balanceDeadline)}`
      : deadlineDate

  const deadlineRowLabel = isAwaitingDeposit
    ? 'Date limite de réception du virement'
    : 'Date limite de paiement du solde'

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-gray-light-200 bg-white p-4 md:p-6">
      <div className="text-lg font-semibold text-gray-light-900 sm:text-2xl">
        Règlement de votre réservation et modalités de paiements
      </div>

      {deadlineLabel && <SummaryRow label={deadlineRowLabel} value={deadlineLabel} />}
      <SummaryRow label="Acomptes versés" value={formatPrice(centsToEuros(paidAmount))} />
      <SummaryRow label="Reste à payer" value={formatPrice(centsToEuros(remainingAmount))} />

      <Separator />

      {/* Le RIB reste affiché tant qu'il reste quelque chose à régler : un dossier
          dont l'acompte est déjà payé se solde aussi par virement, et le masquer
          laissait le client sans aucun moyen de trouver les coordonnées bancaires. */}
      {remainingAmount > 0 && (
        <div className="flex flex-col gap-2">
          <div className="text-sm font-medium text-gray-light-700">
            {isAwaitingDeposit
              ? 'Régler par virement bancaire'
              : 'Régler le solde par virement bancaire'}
          </div>
          <BankDetailsGrid isReunion={isReunion} />
        </div>
      )}

      <div className="mt-1 flex flex-wrap gap-3">
        <Button asChild className="bg-brand-600 hover:bg-brand-700">
          <Link
            search={{
              email: booking.client.email ?? '',
              lastName: booking.passengers[0]?.lastName ?? '',
              dossierNumber: booking.reference,
            }}
            to="/payer-acompte"
          >
            <CreditCard className="mr-2 size-4" />
            Payer un acompte en carte bancaire
          </Link>
        </Button>

        {/* Inconditionnel, comme le bouton CB et le contrat PDF : c'est /payer-acompte qui
            porte le refus (dossier annulé, cumul, soldé…) avec son message au moment du clic. */}
        <Button asChild variant="outline">
          <Link
            search={{
              email: booking.client.email ?? '',
              lastName: booking.passengers[0]?.lastName ?? '',
              dossierNumber: booking.reference,
              mode: 'installments',
            }}
            to="/payer-acompte"
          >
            <CalendarClock className="mr-2 size-4" />
            Payer en 3/4x avec {installmentProviderLabel}
          </Link>
        </Button>
      </div>
    </div>
  )
}

function formatDeadline(isoDate: null | string | undefined): string | undefined {
  if (!isoDate) return undefined
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(new Date(isoDate))
  } catch {
    return isoDate
  }
}
